import { getStraightTimeMultiplier, getSortedOvertimeMultipliers, getPrevailingRateMultiplier, getThreshold } from '@/utils/rateCalculations';
import type { MealBreakConfig, Role, RateMultiplier } from '@/types';
import { isBreakUnpaid } from './markdownVariables';
import { getOrdinal, formatTimeFromMinutes, formatCurrency, formatHours } from './markdownSharedUtils';

export const generateEscalationTableRows = (
  thresholdMinutes: number, 
  cadence: number, 
  rateMultipliers: RateMultiplier[] = []
): string => {
  if (!thresholdMinutes || thresholdMinutes <= 0 || !cadence || cadence <= 0) return '';
  const count = Math.floor(thresholdMinutes / cadence);
  if (count === 0) return ''; // No full periods before escalation
  const rows: string[] = [];
  const maxRows = Math.min(count, 4);

  // Use the rate calculation utilities to get proper ladder progression
  const straightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);
  const hourlyOvertimeMultipliers = getSortedOvertimeMultipliers(rateMultipliers, 'hourly');
  const dayRateOvertimeMultipliers = getSortedOvertimeMultipliers(rateMultipliers, 'day_rate');
  
  // Use hourly multipliers as the primary example, fall back to day rate if no hourly
  const overtimeMultipliers = hourlyOvertimeMultipliers.length > 0 ? hourlyOvertimeMultipliers : dayRateOvertimeMultipliers;
  
  // Get the first two multipliers in the ladder for Level 1 and Level 2 examples
  const level1Multiplier = overtimeMultipliers[0] || { 
    code: 'OT', 
    name: 'Overtime', 
    defaultMultiplier: 1.5,
    perShiftBillingStructures: [],
    perWeekBillingStructures: []
  };
  const level2Multiplier = overtimeMultipliers[1] || overtimeMultipliers[0] || { 
    code: 'DT', 
    name: 'Double Time', 
    defaultMultiplier: 2.0,
    perShiftBillingStructures: [],
    perWeekBillingStructures: []
  };

  const straightTimeExample = `${straightTimeMultiplier.defaultMultiplier}x (${straightTimeMultiplier.name})`;
  const level1Example = `${level1Multiplier.defaultMultiplier}x (${level1Multiplier.name})`;
  const level2Example = `${level2Multiplier.defaultMultiplier}x (${level2Multiplier.name})`;

  for (let i = 0; i < maxRows; i++) {
    const startTime = i * cadence;
    const endTime = (i + 1) * cadence;
    const intervalNum = i + 1;
    const isEscalationPoint = intervalNum === count;
    const isLastDisplayedRow = i === maxRows - 1 && count > 4;

    const offsetText = isEscalationPoint
      ? '+ 1<br/>(Escalation point)'
      : isLastDisplayedRow ? `+ 1<br/>(Surcharge applied ${count - maxRows} more times...)` : '+ 1';

    rows.push(
      `| **${startTime} - ${endTime} mins** | ${intervalNum} | ${offsetText} | ${straightTimeExample} | e.g., ${level1Example} |`
    );
  }
  
  // Add the escalated level example
  if (count > 0) {
    const escalatedTime = Math.floor(count * cadence);
    rows.push(
      `| **After ${escalatedTime} minutes** | ${count + 1}+ | + 2<br/>(Additional offset surcharge) | ${straightTimeExample} | e.g., ${level2Example} |`
    );
  }
  
  return rows.join('\n');
};

interface MultiplierOffsetPeriod {
    violationNumber: number;
    violationStartTime: string;
    startTime: string;
    endTime: string;
    prevailingMultiplierName: string;
    prevailingMultiplierRate: number;
    offset: number;
    appliedMultiplierName: string;
    appliedMultiplierRate: number;
    normalPay: string;
    normalPayCalc: string;
    surchargePay: string;
    surchargePayCalc: string;
    premium: string;
    premiumCalc: string;
    showBreakMarker: number;
    totalMinutes?: number;
}

const buildMultiplierOffsetExampleTableHtml = (
    periods: MultiplierOffsetPeriod[],
    totals: { totalNormalPay: number, totalSurchargePay: number, totalPremium: number },
    isRecurring: boolean,
    isResetScenario: boolean = false,
    breakInfo?: { breakStartTime: string, breakEndTime: string, breakLengthMinutes: number }
): string => {
    let tableHeader = `<thead><tr class="border-b border-border/50">`;
    if (isRecurring) {
        tableHeader += `<th class="px-2 py-1 bg-muted/30 font-semibold text-center">Occurrence</th>`;
    }
    tableHeader += `<th class="px-2 py-1 bg-muted/30 font-semibold text-center">Occurrence Start</th>` +
        `<th class="px-2 py-1 bg-muted/30 font-semibold text-center">Surcharge Period</th>` +
        `<th class="px-2 py-1 bg-muted/30 font-semibold text-center">Prevailing Rate</th>` +
        `<th class="px-2 py-1 bg-muted/30 font-semibold text-center">Offset</th>` +
        `<th class="px-2 py-1 bg-muted/30 font-semibold text-center">Rate Applied</th>` +
        `<th class="px-2 py-1 bg-muted/30 font-semibold text-center">Gross Pay Before Surcharge</th>` +
        `<th class="px-2 py-1 bg-muted/30 font-semibold text-center">Gross Pay After Surcharge</th>` +
        `<th class="px-2 py-1 bg-muted/30 font-semibold text-center">Meal Surcharge Assessed</th>` +
    `</tr></thead>`;

    const tableRows: string[] = [];
    let logicalRowIndex = -1;
    
    const violationGroups = new Map<number, MultiplierOffsetPeriod[]>();
    periods.forEach(p => {
        if (!violationGroups.has(p.violationNumber)) {
            violationGroups.set(p.violationNumber, []);
        }
        violationGroups.get(p.violationNumber)!.push(p);
    });

    periods.forEach((period) => {
        const { violationNumber: periodViolationNumber } = period;
        const violationGroup = violationGroups.get(periodViolationNumber)!;
        const isFirstInViolation = violationGroup[0] === period;
        if (isFirstInViolation) {
            logicalRowIndex++;
        }
        const rowClass = logicalRowIndex % 2 === 0 ? 'logical-odd' : 'logical-even';
        const {
            violationNumber,
            violationStartTime,
            startTime,
            endTime,
            prevailingMultiplierName,
            prevailingMultiplierRate,
            offset,
            appliedMultiplierName,
            appliedMultiplierRate,
            normalPay,
            normalPayCalc,
            surchargePay,
            surchargePayCalc,
            premium,
            premiumCalc,
            showBreakMarker,
        } = period;

        if (showBreakMarker > 0 && breakInfo) {
            tableRows.push(`<tr class="bg-muted/10"><td colspan="9" class="px-2 py-1 align-middle text-center">Meal break taken ${breakInfo.breakStartTime} – ${breakInfo.breakEndTime} (${breakInfo.breakLengthMinutes} minutes)</td></tr>`);
        }

        let rowHtml = `<tr class="${rowClass}">`;

        if (isFirstInViolation) {
            const rowspan = violationGroup.length;
            if (isRecurring) {
                rowHtml += `<td rowspan="${rowspan}" class="px-2 py-1 align-middle text-center"><strong>${getOrdinal(violationNumber)}</strong></td>`;
            }
            rowHtml += `<td rowspan="${rowspan}" class="px-2 py-1 align-middle text-center">${violationStartTime}</td>`;
        }

        rowHtml += `<td class="px-2 py-1 align-middle text-center"><strong>${startTime} – ${endTime}</strong></td>`;
        rowHtml += `<td class="px-2 py-1 align-middle text-center">${prevailingMultiplierName} (${prevailingMultiplierRate}x)</td>`;
        rowHtml += `<td class="px-2 py-1 align-middle text-center">+${offset} level${offset > 1 ? 's' : ''}</td>`;
        rowHtml += `<td class="px-2 py-1 align-middle text-center">${appliedMultiplierName} (${appliedMultiplierRate}x)</td>`;
        rowHtml += `<td class="px-2 py-1 align-middle text-center">$${normalPay}<br/>(${normalPayCalc})</td>`;
        rowHtml += `<td class="px-2 py-1 align-middle text-center">$${surchargePay}<br/>(${surchargePayCalc})</td>`;
        rowHtml += `<td class="px-2 py-1 align-middle text-center"><strong>+$${premium}</strong><br/>(${premiumCalc})</td>`;

        rowHtml += '</tr>';
        tableRows.push(rowHtml);
    });

    const totalNormalPayStr = formatCurrency(totals.totalNormalPay);
    const totalSurchargePayStr = formatCurrency(totals.totalSurchargePay);
    const totalPremiumStr = formatCurrency(totals.totalPremium);
    
    let totalsRow = `<tr class="border-t-2 border-border/50 font-semibold">`;
    if (isRecurring) {
        totalsRow += `<td class="px-2 py-1 align-middle text-left border-l-0 border-r-0"><strong>TOTALS</strong></td>`;
        totalsRow += `<td class="px-2 py-1 align-middle text-center border-l-0 border-r-0"></td>`;
        totalsRow += `<td class="px-2 py-1 align-middle text-center border-l-0 border-r-0"></td>`;
        totalsRow += `<td class="px-2 py-1 align-middle text-center border-l-0 border-r-0"></td>`;
        totalsRow += `<td class="px-2 py-1 align-middle text-center border-l-0 border-r-0"></td>`;
        totalsRow += `<td class="px-2 py-1 align-middle text-center border-l-0 border-r-0"></td>`;
    } else {
        totalsRow += `<td class="px-2 py-1 align-middle text-left border-l-0 border-r-0"><strong>TOTALS</strong></td>`;
        totalsRow += `<td class="px-2 py-1 align-middle text-center border-l-0 border-r-0"></td>`;
        totalsRow += `<td class="px-2 py-1 align-middle text-center border-l-0 border-r-0"></td>`;
        totalsRow += `<td class="px-2 py-1 align-middle text-center border-l-0 border-r-0"></td>`;
        totalsRow += `<td class="px-2 py-1 align-middle text-center border-l-0 border-r-0"></td>`;
    }
    totalsRow += `<td class="px-2 py-1 align-middle text-center border-l-0 border-r-0"><strong>$${totalNormalPayStr}</strong></td>`;
    totalsRow += `<td class="px-2 py-1 align-middle text-center border-l-0 border-r-0"><strong>$${totalSurchargePayStr}</strong></td>`;
    totalsRow += `<td class="px-2 py-1 align-middle text-center border-l-0 border-r-0"><strong>$${totalPremiumStr}</strong></td>`;
    totalsRow += `</tr>`;
    tableRows.push(totalsRow);

    return `<table class="table-auto border-collapse border border-border/50 text-xs w-full mb-3">${tableHeader}<tbody>${tableRows.join('')}</tbody></table>`;
};

interface MultiplierOffsetParams {
    safeConfig: MealBreakConfig;
    sampleRole: Role;
    rateMultipliers: RateMultiplier[];
    minimumBillableHours: number;
}

export function calculateMultiplierOffsetExamples({
    safeConfig,
    sampleRole,
    rateMultipliers,
    minimumBillableHours,
}: MultiplierOffsetParams) {
    const mealDeadlineHours = Number(safeConfig.mealDeadlineHours) || 5;
    const stepUpAfterMinutes = Number(safeConfig.stepUpAfterMinutes) || 60;
    const useAdditionalOffset = safeConfig.useAdditionalOffset;
    const baseHourlyRateValue = sampleRole.billingStructure === 'day_rate'
        ? (sampleRole.rate || 500) / minimumBillableHours
        : (sampleRole.rate || 50);
    
    const QUARTER_HOUR = 15;
    // Display interval for table rows — ladder_offset has no billing cadence,
    // so we use quarter-hour ticks purely for visualization granularity.
    const displayInterval = QUARTER_HOUR;
    let minimumDuration;

    if (useAdditionalOffset) {
      // We need enough time to show the escalation event plus some time after it.
      minimumDuration = (stepUpAfterMinutes || 0) + displayInterval;
    } else {
      // For non-escalation, just show a few display intervals.
      minimumDuration = Math.ceil(3.5 * displayInterval);
    }

    // Round the required duration UP to the nearest quarter hour.
    const violationDurationMinutes = Math.ceil(minimumDuration / QUARTER_HOUR) * QUARTER_HOUR;
    
    const allStraightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);
    const allOvertimeMultipliers = getSortedOvertimeMultipliers(rateMultipliers, sampleRole.billingStructure);
    const allMultipliers = [allStraightTimeMultiplier, ...allOvertimeMultipliers];
  
    const firstOvertimeMultiplierForCalc = allOvertimeMultipliers[0] || { 
        code: 'OT', 
        name: 'Overtime', 
        defaultMultiplier: 1.5,
        perShiftBillingStructures: ['hourly', 'day_rate'],
        perWeekBillingStructures: ['hourly']
      };
    
    // This will hold the raw, fine-grained surcharge calculations for accurate totals.
    const rawSurchargeCalculations: Array<{ 
      startTime: string;
      endTime: string;
      normalPay: number;
      surchargePay: number;
      premium: number;
      appliedMultiplierName: string;
      appliedMultiplierRate: number;
      prevailingMultiplierName: string;
      prevailingMultiplierRate: number;
      offset: number;
      violationNumber: number;
      durationMinutes: number;
    }> = [];
  
    // Compounding and reset simulation setup
    const violationIntervalMinutes = mealDeadlineHours * 60;
    const firstViolationStart = mealDeadlineHours * 60; // starts at meal window threshold
    const violationsSimulatedCount = safeConfig.recurringMealDeadline ? 2 : 1; // simulate 2 windows if recurring is enabled
    const violationStarts: number[] = Array.from({ length: violationsSimulatedCount }, (_, i) => firstViolationStart + i * violationIntervalMinutes);
  
    // End the continuous work segment a bit into the last simulated violation
    const lastViolationStart = violationStarts[violationStarts.length - 1];
    const simEndMinutes = lastViolationStart + violationDurationMinutes; // keep last violation length consistent with escalation demo
  
    // Build global split points: cadence ticks, OT thresholds, violation starts, escalation points, start/end
    const splitPointsSet = new Set<number>();
  
    // Always split on the first violation start and final end
    splitPointsSet.add(firstViolationStart);
    splitPointsSet.add(simEndMinutes);
  
    // Display ticks for table row granularity (not billing cadences — ladder_offset is continuous)
    const tickStart = firstViolationStart;
    const maxIterations = Math.ceil((simEndMinutes - tickStart) / Math.max(displayInterval, 1)) + 10;
    let iterations = 0;
    for (let t = tickStart; t < simEndMinutes && iterations < maxIterations; t += displayInterval) {
      splitPointsSet.add(t);
      iterations++;
      if (splitPointsSet.size > 10000) {
        console.warn('Split points set exceeded safety limit, breaking loop');
        break;
      }
    }
  
    // Overtime thresholds (absolute minutes since shift start)
    allOvertimeMultipliers
      .map(m => getThreshold(m, sampleRole.billingStructure) * 60)
      .filter(t => t > firstViolationStart && t < simEndMinutes)
      .forEach(t => splitPointsSet.add(t));
  
    // Additional violation starts (for compounding)
    violationStarts.slice(1).forEach(v => {
      if (v > firstViolationStart && v < simEndMinutes) splitPointsSet.add(v);
    });
  
    // Escalation points for each violation (if enabled)
    if (useAdditionalOffset && stepUpAfterMinutes > 0) {
      violationStarts.forEach(vStart => {
        const ep = vStart + stepUpAfterMinutes;
        if (ep > firstViolationStart && ep < simEndMinutes) splitPointsSet.add(ep);
      });
    }
  
    const splitPoints = Array.from(splitPointsSet).sort((a, b) => a - b);
  
    // Track the highest offset applied so far to compound across subsequent violations
    let maxOffsetSoFar = 0;
    let baseOffsetForCurrentViolation = 0; // This will hold the base offset for the current violation period
  
    // Walk consecutive split points, compute compounding offset and premiums
    for (let s = 0; s < splitPoints.length - 1; s++) {
      const subStart = splitPoints[s];
      const subEnd = splitPoints[s + 1];
      const subDur = subEnd - subStart;
      if (subDur <= 0 || subStart < firstViolationStart) continue;
  
      // Active violations = number of violation windows already missed and still no break
      const activeViolations = violationStarts.filter(v => v <= subStart).length;
      if (activeViolations <= 0) continue;
  
      // Latest violation start
      const latestViolationStart = violationStarts
        .filter(v => v <= subStart)
        .reduce((a, b) => Math.max(a, b), firstViolationStart);
  
      const isNewViolationStart = violationStarts.includes(subStart);
  
      // Base compounding:
      // - At the start of a new violation, calculate its base offset.
      // - Otherwise, continue using the base offset for the current violation.
      if (isNewViolationStart) {
        if (activeViolations === 1) {
          baseOffsetForCurrentViolation = 1;
        } else {
          // New violation starts, so its base is one more than the max from all previous.
          baseOffsetForCurrentViolation = maxOffsetSoFar + 1;
        }
      }
  
      // Escalation applies within the current (latest) violation window once its own threshold is reached.
      const escalatedExtra =
        (useAdditionalOffset &&
          stepUpAfterMinutes > 0 &&
          (subStart - latestViolationStart) >= stepUpAfterMinutes)
          ? 1
          : 0;
  
      const offsetLevels = baseOffsetForCurrentViolation + escalatedExtra;
  
      const subStartHours = subStart / 60;
  
      const periodPrevailingRate = getPrevailingRateMultiplier(
        subStartHours,
        rateMultipliers,
        sampleRole.billingStructure
      );
  
      const prevailingIndex = Math.max(0, allMultipliers.findIndex(m => m.code === periodPrevailingRate.code));
      const offsetIndex = prevailingIndex + offsetLevels;
  
      let surchargeMultiplier: RateMultiplier;
      if (offsetIndex < allMultipliers.length) {
        surchargeMultiplier = allMultipliers[offsetIndex];
      } else {
        surchargeMultiplier = allMultipliers[allMultipliers.length - 1] || (firstOvertimeMultiplierForCalc as RateMultiplier);
      }
  
      const multiplier = surchargeMultiplier.defaultMultiplier ?? 0;
      const premiumMultiplier = multiplier - (periodPrevailingRate.defaultMultiplier ?? 0);

      const baseHourlyRate = sampleRole.billingStructure === 'day_rate'
        ? (sampleRole.rate || 500) / minimumBillableHours
        : (sampleRole.rate || 50);

      const normalPay = Math.round((baseHourlyRate * (periodPrevailingRate.defaultMultiplier ?? 0) * subDur / 60) * 100) / 100;
      const surchargePay = Math.round((baseHourlyRate * multiplier * subDur / 60) * 100) / 100;
      const premium = Math.round((surchargePay - normalPay) * 100) / 100;

      const startTime = formatTimeFromMinutes(subStart);
      const endTime = formatTimeFromMinutes(subEnd);

      rawSurchargeCalculations.push({
        startTime,
        endTime,
        normalPay,
        surchargePay,
        premium,
        appliedMultiplierName: surchargeMultiplier.name,
        appliedMultiplierRate: surchargeMultiplier.defaultMultiplier ?? 0,
        prevailingMultiplierName: periodPrevailingRate.name,
        prevailingMultiplierRate: periodPrevailingRate.defaultMultiplier ?? 0,
        offset: offsetLevels,
        violationNumber: activeViolations,
        durationMinutes: subDur
      });
  
      // Update the maximum offset seen so far to compound in subsequent violations
      maxOffsetSoFar = Math.max(maxOffsetSoFar, offsetLevels);
    }
  
    // Simulated reset/break info for markdown
    const simulatedResetTime = formatTimeFromMinutes(simEndMinutes);
    const violationTotalMinutes = simEndMinutes - firstViolationStart;
    const compoundOffsetSummary = safeConfig.useAdditionalOffset
      ? `Base offsets +1 per missed window (e.g., +${violationsSimulatedCount} by the last missed window), plus an additional +1 after ${stepUpAfterMinutes} minutes into the latest violation`
      : `Base offsets +1 per missed window (e.g., +${violationsSimulatedCount} by the last missed window)`;
  
    // Violation start time display (with grace) for compounding example
    const violationStartTimesByNumber: Record<number, string> = {};
    violationStarts.forEach((vStart, i) => {
      violationStartTimesByNumber[i + 1] = formatTimeFromMinutes(vStart);
    });
  
    // Build a second example with a hard reset (break) between violations
    const breakLengthMinutes = Number(safeConfig.mealBreakLengthMinutes) || 30;
  
    const violation1StartReset = firstViolationStart;
    const violation1EndReset = violation1StartReset + violationDurationMinutes;
  
    const breakStartReset = violation1EndReset;
    const breakEndReset = breakStartReset + breakLengthMinutes;
  
    const violation2StartReset = breakEndReset + violationIntervalMinutes;
    const rawSimEndMinutesReset = violation2StartReset + violationDurationMinutes;
    // Round up to the nearest hour for a clean end time
    const simEndMinutesReset = Math.ceil(rawSimEndMinutesReset / 60) * 60;
  
    const splitPointsSetReset = new Set<number>([
      violation1StartReset,
      breakStartReset,
      breakEndReset,
      violation2StartReset,
      simEndMinutesReset
    ]);
  
    // Display ticks for table row granularity (not billing cadences — ladder_offset is continuous)
    const maxIterationsReset = Math.ceil((simEndMinutesReset - violation1StartReset) / Math.max(displayInterval, 1)) + 10;
    let iterationsReset = 0;
    for (let t = violation1StartReset; t < simEndMinutesReset && iterationsReset < maxIterationsReset; t += displayInterval) {
      splitPointsSetReset.add(t);
      iterationsReset++;
      if (splitPointsSetReset.size > 10000) {
        console.warn('Split points set (reset) exceeded safety limit, breaking loop');
        break;
      }
    }
  
    // Overtime thresholds
    allOvertimeMultipliers
      .map(m => getThreshold(m, sampleRole.billingStructure) * 60) // threshold in minutes worked
      .forEach(thresholdMinutes => {
        let absoluteThresholdMinutes = thresholdMinutes;
        // An unpaid break pushes back the absolute time at which a work-hour threshold is met.
        const workedMinutesAtBreakStart = breakStartReset;
        if (isBreakUnpaid(safeConfig.breakBillingPolicy, breakLengthMinutes) && thresholdMinutes > workedMinutesAtBreakStart) {
            absoluteThresholdMinutes += breakLengthMinutes;
        }
  
        if (absoluteThresholdMinutes > violation1StartReset && absoluteThresholdMinutes < simEndMinutesReset) {
            splitPointsSetReset.add(absoluteThresholdMinutes);
        }
      });
  
    // Escalation points per violation (if enabled)
    if (useAdditionalOffset && stepUpAfterMinutes > 0) {
      [violation1StartReset, violation2StartReset].forEach(vStart => {
        const ep = vStart + stepUpAfterMinutes;
        if (ep > violation1StartReset && ep < simEndMinutesReset) splitPointsSetReset.add(ep);
      });
    }
  
    const splitPointsReset = Array.from(splitPointsSetReset).sort((a, b) => a - b);
  
    const rawSurchargeCalculationsReset: Array<{
      startTime: string;
      endTime: string;
      normalPay: number;
      surchargePay: number;
      premium: number;
      appliedMultiplierName: string;
      appliedMultiplierRate: number;
      prevailingMultiplierName: string;
      prevailingMultiplierRate: number;
      offset: number;
      violationNumber: number;
      durationMinutes: number;
    }> = [];
  
    for (let s = 0; s < splitPointsReset.length - 1; s++) {
      const subStart = splitPointsReset[s];
      const subEnd = splitPointsReset[s + 1];
      const subDur = subEnd - subStart;
      if (subDur <= 0) continue;
  
      // Skip the break itself
      if (subStart >= breakStartReset && subStart < breakEndReset) continue;
  
      let currentViolationStart: number | null = null;
      let violationNumberForRow = 0;
  
      if (subStart >= violation1StartReset && subStart < violation1EndReset) {
        currentViolationStart = violation1StartReset;
        violationNumberForRow = 1;
      } else if (subStart >= violation2StartReset && subStart < simEndMinutesReset) {
        currentViolationStart = violation2StartReset;
        violationNumberForRow = 2;
      } else {
        continue; // outside of violations
      }
  
      // Reset behavior: base offset always +1 per violation, no compounding between violations
      const baseOffset = 1;
  
      // Escalation within the current violation window
      const escalatedExtra =
        (useAdditionalOffset &&
          stepUpAfterMinutes > 0 &&
          (subStart - currentViolationStart) >= stepUpAfterMinutes) ? 1 : 0;
  
      const offsetLevels = baseOffset + escalatedExtra;
  
      let subStartHours = subStart / 60;
      if (isBreakUnpaid(safeConfig.breakBillingPolicy, breakLengthMinutes) && subStart >= breakEndReset) {
        subStartHours -= breakLengthMinutes / 60;
      }
      const periodPrevailingRate = getPrevailingRateMultiplier(
        subStartHours,
        rateMultipliers,
        sampleRole.billingStructure
      );
  
      const prevailingIndex = Math.max(0, allMultipliers.findIndex(m => m.code === periodPrevailingRate.code));
      const offsetIndex = prevailingIndex + offsetLevels;
  
      let surchargeMultiplier: RateMultiplier;
      if (offsetIndex < allMultipliers.length) {
        surchargeMultiplier = allMultipliers[offsetIndex];
      } else {
        surchargeMultiplier = allMultipliers[allMultipliers.length - 1] || firstOvertimeMultiplierForCalc;
      }
  
      const multiplier = surchargeMultiplier.defaultMultiplier ?? 0;
      const premiumMultiplier = multiplier - (periodPrevailingRate.defaultMultiplier ?? 0);

      const baseHourlyRate = sampleRole.billingStructure === 'day_rate'
        ? (sampleRole.rate || 500) / minimumBillableHours
        : (sampleRole.rate || 50);

      const normalPay = Math.round((baseHourlyRate * (periodPrevailingRate.defaultMultiplier ?? 0) * subDur / 60) * 100) / 100;
      const surchargePay = Math.round((baseHourlyRate * multiplier * subDur / 60) * 100) / 100;
      const premium = Math.round((surchargePay - normalPay) * 100) / 100;

      const startTime = formatTimeFromMinutes(subStart);
      const endTime = formatTimeFromMinutes(subEnd);

      rawSurchargeCalculationsReset.push({
        startTime,
        endTime,
        normalPay,
        surchargePay,
        premium,
        appliedMultiplierName: surchargeMultiplier.name,
        appliedMultiplierRate: surchargeMultiplier.defaultMultiplier ?? 0,
        prevailingMultiplierName: periodPrevailingRate.name,
        prevailingMultiplierRate: periodPrevailingRate.defaultMultiplier ?? 0,
        offset: offsetLevels,
        violationNumber: violationNumberForRow,
        durationMinutes: subDur
      });
    }
  
    // Precompute reset example display times and violation start times (with grace)
    const resetBreakStartTime = formatTimeFromMinutes(breakStartReset);
    const resetBreakEndTime = formatTimeFromMinutes(breakEndReset);
    const resetViolation2StartTime = formatTimeFromMinutes(violation2StartReset);
    const resetShiftEndTime = formatTimeFromMinutes(simEndMinutesReset);
  
    const resetViolationStartTimesByNumber: Record<number, string> = {
      1: formatTimeFromMinutes(violation1StartReset),
      2: formatTimeFromMinutes(violation2StartReset),
    };
  
    // Group reset periods similarly, annotate first row per violation with start time,
    // and mark a single break row before the first row of Violation #2+
    const seenFirstRowByViolationReset = new Set<number>();
    const mergedPeriodsReset = rawSurchargeCalculationsReset.reduce((acc, current) => {
      const last = acc[acc.length - 1];
      if (last && last.prevailingMultiplierName === current.prevailingMultiplierName && last.offset === current.offset && last.violationNumber === current.violationNumber) {
        last.endTime = current.endTime;
        last.normalPay += current.normalPay;
        last.surchargePay += current.surchargePay;
        last.premium += current.premium;
        last.totalMinutes += current.durationMinutes;
      } else {
        const isFirstOfThisViolation = !seenFirstRowByViolationReset.has(current.violationNumber);
        let violationStartTime = '';
        if (isFirstOfThisViolation) {
          violationStartTime = resetViolationStartTimesByNumber[current.violationNumber] || '';
          seenFirstRowByViolationReset.add(current.violationNumber);
        }
        acc.push({
          startTime: current.startTime,
          endTime: current.endTime,
          normalPay: current.normalPay,
          surchargePay: current.surchargePay,
          premium: current.premium,
          appliedMultiplierName: current.appliedMultiplierName,
          appliedMultiplierRate: current.appliedMultiplierRate,
          prevailingMultiplierName: current.prevailingMultiplierName,
          prevailingMultiplierRate: current.prevailingMultiplierRate,
          offset: current.offset,
          violationNumber: current.violationNumber,
          violationStartTime,
          showBreakMarker: (isFirstOfThisViolation && current.violationNumber > 1) ? 1 : 0,
          totalMinutes: current.durationMinutes,
        });
      }
      return acc;
    }, [] as Array<{
      startTime: string;
      endTime: string;
      normalPay: number;
      surchargePay: number;
      premium: number;
      appliedMultiplierName: string;
      appliedMultiplierRate: number;
      prevailingMultiplierName: string;
      prevailingMultiplierRate: number;
      offset: number;
      violationNumber: number;
      violationStartTime: string;
      showBreakMarker: number;
      totalMinutes: number;
    }>);

    const surchargePeriodsReset = mergedPeriodsReset.map(period => {
      const hrs = (period.totalMinutes / 60);
      const normalPayStr = formatCurrency(period.normalPay);
      const surchargePayStr = formatCurrency(period.surchargePay);
      const premiumStr = formatCurrency(period.premium);
    
      const baseHourlyFormatted = formatCurrency(baseHourlyRateValue);
      const normalPayCalc = `${formatHours(hrs)}h × $${baseHourlyFormatted}/hr × ${period.prevailingMultiplierRate}x`;
      const surchargePayCalc = `${formatHours(hrs)}h × $${baseHourlyFormatted}/hr × ${period.appliedMultiplierRate}x`;
      const premiumCalc = `$${surchargePayStr} - $${normalPayStr}`;
      
      return {
        ...period,
        normalPay: normalPayStr,
        surchargePay: surchargePayStr,
        premium: premiumStr,
        normalPayCalc,
        surchargePayCalc,
        premiumCalc,
        showBreakMarker: period.showBreakMarker,
        violationStartTime: period.violationStartTime
      };
    });
  
    const totalNormalPayReset = rawSurchargeCalculationsReset.reduce((sum, p) => sum + p.normalPay, 0);
    const totalSurchargePayReset = rawSurchargeCalculationsReset.reduce((sum, p) => sum + p.surchargePay, 0);
    const totalPremiumReset = rawSurchargeCalculationsReset.reduce((sum, p) => sum + p.premium, 0);
  
    // Group the raw calculations into logical blocks for display, annotate first row per violation with start time
    const seenFirstRowByViolation = new Set<number>();
    const mergedPeriods = rawSurchargeCalculations.reduce((acc, current) => {
      const last = acc[acc.length - 1];
      if (last && last.prevailingMultiplierName === current.prevailingMultiplierName && last.offset === current.offset && last.violationNumber === current.violationNumber) {
        // Merge with the last period
        last.endTime = current.endTime;
        last.normalPay += current.normalPay;
        last.surchargePay += current.surchargePay;
        last.premium += current.premium;
        last.totalMinutes += current.durationMinutes;
      } else {
        // Start a new period
        let violationStartTime = '';
        if (!seenFirstRowByViolation.has(current.violationNumber)) {
          violationStartTime = violationStartTimesByNumber[current.violationNumber] || '';
          seenFirstRowByViolation.add(current.violationNumber);
        }
        acc.push({
          startTime: current.startTime,
          endTime: current.endTime,
          normalPay: current.normalPay,
          surchargePay: current.surchargePay,
          premium: current.premium,
          appliedMultiplierName: current.appliedMultiplierName,
          appliedMultiplierRate: current.appliedMultiplierRate,
          prevailingMultiplierName: current.prevailingMultiplierName,
          prevailingMultiplierRate: current.prevailingMultiplierRate,
          offset: current.offset,
          violationNumber: current.violationNumber,
          violationStartTime: violationStartTime,
          totalMinutes: current.durationMinutes,
        });
      }
      return acc;
    }, [] as Array<{
      startTime: string;
      endTime: string;
      normalPay: number;
      surchargePay: number;
      premium: number;
      appliedMultiplierName: string;
      appliedMultiplierRate: number;
      prevailingMultiplierName: string;
      prevailingMultiplierRate: number;
      offset: number;
      violationNumber: number;
      violationStartTime: string;
      totalMinutes: number;
    }>);

    const surchargePeriods = mergedPeriods.map(period => {
      const hrs = (period.totalMinutes / 60);
      const normalPayStr = formatCurrency(period.normalPay);
      const surchargePayStr = formatCurrency(period.surchargePay);
      const premiumStr = formatCurrency(period.premium);
      
      const baseHourlyFormatted = formatCurrency(baseHourlyRateValue);
      const normalPayCalc = `${formatHours(hrs)}h × $${baseHourlyFormatted}/hr × ${period.prevailingMultiplierRate}x`;
      const surchargePayCalc = `${formatHours(hrs)}h × $${baseHourlyFormatted}/hr × ${period.appliedMultiplierRate}x`;
      const premiumCalc = `$${surchargePayStr} - $${normalPayStr}`;
    
      return {
        ...period,
        normalPay: normalPayStr,
        surchargePay: surchargePayStr,
        premium: premiumStr,
        normalPayCalc,
        surchargePayCalc,
        premiumCalc,
        showBreakMarker: 0,
      };
    });

    const totalNormalPay = rawSurchargeCalculations.reduce((sum, p) => sum + p.normalPay, 0);
    const totalSurchargePay = rawSurchargeCalculations.reduce((sum, p) => sum + p.surchargePay, 0);
    const totalPremium = rawSurchargeCalculations.reduce((sum, p) => sum + p.premium, 0);

    const multiplierOffsetTableHtml = buildMultiplierOffsetExampleTableHtml(
        surchargePeriods,
        { totalNormalPay, totalSurchargePay, totalPremium },
        !!safeConfig.recurringMealDeadline,
        false
    );

    const multiplierOffsetTableResetHtml = buildMultiplierOffsetExampleTableHtml(
        surchargePeriodsReset,
        { totalNormalPay: totalNormalPayReset, totalSurchargePay: totalSurchargePayReset, totalPremium: totalPremiumReset },
        !!safeConfig.recurringMealDeadline,
        true,
        { breakStartTime: resetBreakStartTime, breakEndTime: resetBreakEndTime, breakLengthMinutes }
    );

    return {
        totalNormalPay,
        totalSurchargePay,
        totalPremium,
        violationsSimulatedCount,
        simulatedResetTime,
        violationTotalMinutes,
        compoundOffsetSummary,
        totalNormalPayReset,
        totalSurchargePayReset,
        totalPremiumReset,
        resetBreakStartTime,
        resetBreakEndTime,
        resetViolation2StartTime,
        resetShiftEndTime,
        multiplierOffsetTableHtml,
        multiplierOffsetTableResetHtml,
    };
}
